// marketplace.jsx — 数字人市场首页
var { useState, useMemo, useEffect, useRef, useCallback, useContext } = React;  // 5/25 v10.74: var — multi .jsx 共 scope const 撞

/* ---------------- Portraits ----------------
   Since we don't have 100 real photos, we generate a stylized portrait
   per avatar from a hash of its slug + category hue. Looks intentional,
   not slop. Real motion mp4s slot in later.
*/
function avatarHash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function hsl(h, s, l, a = 1) { return `hsla(${h}, ${s}%, ${l}%, ${a})`; }

// 5/17 增量 motion ship: 客户端拉 OSS motion/index.json (slug → mp4 URL).
// 每段 i2v 生成完写一条到 index.json, marketplace 客户端 10s 内 swap portrait → motion.
window.__MOTION_INDEX = window.__MOTION_INDEX || {};
window.__MOTION_INDEX_LOADED = false;
// 5/17 OSS bucket BlockPublicAccess private, 用 10y signed URL hardcode
// (server-side AK 签 once, client lifecycle 内 valid). index.json 内含
// 每个 mp4 的 signed URL (motion_signer.py 每 20s 重写).
// 5/17 混合内容 fix: index URL + mp4 URL 全 https:// (老 http:// 被
// 浏览器静默 block 致 motion 0 播放).
const MOTION_INDEX_URL = "https://wanwushop-proai-img.oss-cn-qingdao.aliyuncs.com/digital-avatar%2Fmotion%2Findex.json?OSSAccessKeyId=LTAI5tH5uzaGR9FF1we5BRPa&Expires=2094365583&Signature=apEydH0I8DMf56caXRgv4xJ1LZ0%3D";
function __toHttps(u) { return typeof u === "string" ? u.replace(/^http:\/\//i, "https://") : u; }
window.__fetchMotionIndex = async function fetchMotionIndex() {
  try {
    const url = MOTION_INDEX_URL + "&_t=" + Math.floor(Date.now() / 30000); // cache buster 30s
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) return;
    const d = await r.json();
    const m = d.motions || {};
    for (const k of Object.keys(m)) m[k] = __toHttps(m[k]);
    window.__MOTION_INDEX = m;
    window.__MOTION_INDEX_LOADED = true;
    return m;
  } catch (e) { return; }
};
window.__fetchMotionIndex();
setInterval(window.__fetchMotionIndex, 30000); // 30s 增量轮询

// 5/17 单音轨 + 视口节流: 全局只一张卡可出声 (audio active slug context).
const AudioCtx = React.createContext({ activeSlug: null, setActiveSlug: () => {} });

const AvatarPortrait = React.forwardRef(function AvatarPortrait({ avatar }, fwdRef) {
  const seed = avatarHash(avatar.slug);
  const baseHue = (window.CATEGORIES.find(c => c.id === avatar.cat)?.hue) ?? 20;
  // jitter per-avatar
  const h1 = (baseHue + (seed % 14) - 7 + 360) % 360;
  const h2 = (h1 + 24 + (seed % 10)) % 360;
  const h3 = (h1 - 18 + 360) % 360;

  const isXiaocheng = avatar.slug === "xiaocheng";
  const hasPhoto = avatar.portrait && avatar.portrait.length > 0;

  // 5/17 motion 已生成则直接替代静态图 (autoplay/muted/loop, 无需 hover)
  // 强制 re-render: 客户端 30s 拉 OSS index.json, motion 新进时 re-render.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // 5/17 IntersectionObserver 视口节流: 不在视口里只渲染静态图 (video unmount,
  // 释放解码器). 阈值 0.2 兼顾"接近进入"的预加载.
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = rootRef.current; if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0.2, rootMargin: "120px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const { activeSlug, setActiveSlug } = useContext(AudioCtx);
  const isAudioActive = activeSlug === avatar.slug;

  // iOS Safari audio policy: 点击 handler 内必须 sync 调 v.muted=false + v.play(),
  // 仅 React prop 变 muted 不生效. forwardRef 让 AvatarCard 把 click 委派下来.
  React.useImperativeHandle(fwdRef, () => ({
    toggleAudio() {
      const v = videoRef.current;
      if (isAudioActive) {
        setActiveSlug(null);
        if (v) v.muted = true;
      } else {
        setActiveSlug(avatar.slug);
        if (v) {
          v.muted = false;
          v.volume = 1;
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        }
      }
    }
  }), [isAudioActive, avatar.slug, setActiveSlug]);

  const motionUrl = window.__MOTION_INDEX[avatar.slug];
  const showMotion = !!motionUrl && inView;

  // a few different "lighting" styles based on art style
  const stylePattern = avatar.style;

  return (
    <div className="mk-card-portrait"
      ref={rootRef}
      style={{
      background: `
        radial-gradient(120% 80% at 30% 25%, ${hsl(h2, 60, 26)} 0%, ${hsl(h1, 70, 12)} 55%, #000 100%)
      `,
    }}>
      {/* 5/17 img 始终垫底 — video 解码空帧/stall/unmount 时露出, 不再变黑.
          取消 loading=lazy (React visibleCount+IO 已 lazy 渲染卡片本身) +
          fetchpriority high, 保证 bg img 先于 video metadata 下来. */}
      {hasPhoto ? (
        <img
          src={avatar.portrait}
          alt={avatar.name}
          decoding="async"
          fetchpriority="high"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            objectPosition: isXiaocheng ? "50% 18%" : "50% 22%",
            filter: "saturate(1.06) contrast(1.03)",
          }}
        />
      ) : (
        <GenerativePortrait avatar={avatar} h1={h1} h2={h2} h3={h3} seed={seed} />
      )}
      {/* motion 覆盖层 — 视口内才挂, 滚出去 unmount 释放解码器 */}
      {showMotion && (
        <video
          key={motionUrl}
          ref={videoRef}
          src={motionUrl}
          autoPlay
          muted={!isAudioActive}
          loop
          playsInline
          preload="metadata"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            objectPosition: isXiaocheng ? "50% 18%" : "50% 22%",
          }}
        />
      )}
      {/* audio active 指示 (右上小喇叭) */}
      {showMotion && isAudioActive && (
        <div style={{
          position: "absolute", top: 10, right: 10, zIndex: 3,
          background: "linear-gradient(180deg, #F9A05D, #DD3C28)",
          color: "#fff",
          fontSize: 11, padding: "4px 8px", borderRadius: 999,
          display: "flex", alignItems: "center", gap: 4,
          fontWeight: 700,
          boxShadow: "0 4px 12px -4px rgba(220,80,40,0.6)",
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z"/></svg>
          播放中
        </div>
      )}

      {/* style-specific overlay */}
      {stylePattern === "pixel" && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(0deg, rgba(0,0,0,0.10) 50%, transparent 50%), linear-gradient(90deg, rgba(0,0,0,0.10) 50%, transparent 50%)",
          backgroundSize: "8px 8px, 8px 8px",
          mixBlendMode: "overlay",
        }}/>
      )}
      {stylePattern === "ink" && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.025' numOctaves='2'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
          mixBlendMode: "soft-light",
          opacity: 0.42,
        }}/>
      )}
      {stylePattern === "manga" && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "repeating-radial-gradient(circle at 30% 30%, rgba(255,255,255,0.06) 0 1px, transparent 1px 3px)",
          mixBlendMode: "screen",
        }}/>
      )}
      {stylePattern === "abstract" && (
        <div style={{
          position: "absolute", inset: 0,
          background: `conic-gradient(from ${seed % 360}deg at 50% 50%, ${hsl(h2, 80, 50, 0.35)}, transparent 30%, ${hsl(h3, 80, 50, 0.30)}, transparent 60%)`,
          mixBlendMode: "screen",
          opacity: 0.6,
        }}/>
      )}

      {/* vignette */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(80% 60% at 50% 40%, transparent 50%, rgba(0,0,0,0.5) 100%)",
        pointerEvents: "none",
      }}/>
    </div>
  );
});

/* Stylized "TV studio" portrait silhouette — figures, not faces.
   Different poses + hair shapes are deterministically chosen per avatar.
*/
function GenerativePortrait({ avatar, h1, h2, h3, seed }) {
  const skinHue = 24 + (seed % 8);
  const skin = hsl(skinHue, 38, 62);
  const skinShadow = hsl(skinHue, 32, 38);
  const hairHue = avatar.country === "CN" || avatar.country === "JP" || avatar.country === "KR" || avatar.country === "VN" || avatar.country === "TH" || avatar.country === "MN" ? 25 : 30 + (seed % 30);
  const hair = avatar.gender === "非二元"
    ? hsl((h2 + 200) % 360, 60, 55)
    : avatar.country === "IN" ? hsl(28, 30, 22) : hsl(hairHue, 18 + (seed % 20), 14 + (seed % 18));
  const hairLight = hsl(hairHue, 14, 38);
  const clothHue = (h1 + 180 + (seed % 30)) % 360;
  const cloth = hsl(clothHue, 32, 26);
  const clothLight = hsl(clothHue, 40, 38);

  // Female vs male hair shape (rough)
  const hairShape = avatar.gender === "女" ? "long" : avatar.gender === "男" ? "short" : "punk";
  const accessory = seed % 7; // headphones, hat, glasses, none variants

  return (
    <svg className="mk-portrait-svg" viewBox="0 0 200 356" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
      <defs>
        <radialGradient id={`bg-${avatar.slug}`} cx="50%" cy="32%" r="60%">
          <stop offset="0%"  stopColor={hsl(h2, 55, 30)} />
          <stop offset="60%" stopColor={hsl(h1, 60, 14)} />
          <stop offset="100%" stopColor="#000" />
        </radialGradient>
        <linearGradient id={`light-${avatar.slug}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hsl(h2, 70, 60, 0.18)} />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
        <linearGradient id={`fade-${avatar.slug}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="60%" stopColor="transparent" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.55)" />
        </linearGradient>
      </defs>

      <rect width="200" height="356" fill={`url(#bg-${avatar.slug})`} />
      <rect width="200" height="356" fill={`url(#light-${avatar.slug})`} />

      {/* studio backlight halo */}
      <ellipse cx="100" cy="120" rx="80" ry="70" fill={hsl(h2, 80, 50, 0.18)} />

      {/* SHOULDERS / TORSO */}
      <path d={`M 20 360 L 30 250 Q 60 220 100 215 Q 140 220 170 250 L 180 360 Z`} fill={cloth} />
      <path d={`M 30 280 Q 60 250 100 245 Q 140 250 170 280`} stroke={clothLight} strokeWidth="2" fill="none" opacity="0.5"/>
      {/* collar/neckline */}
      <path d={`M 80 230 Q 100 250 120 230 L 120 250 L 80 250 Z`} fill={skinShadow} opacity="0.6"/>

      {/* NECK */}
      <rect x="88" y="200" width="24" height="32" fill={skin} />
      <rect x="88" y="222" width="24" height="10" fill={skinShadow} opacity="0.4" />

      {/* HEAD */}
      <ellipse cx="100" cy="165" rx="42" ry="50" fill={skin} />
      {/* face shadow side */}
      <path d={`M 100 115 Q 142 130 142 165 Q 142 200 100 215 Z`} fill={skinShadow} opacity="0.18" />

      {/* HAIR */}
      {hairShape === "long" && (
        <>
          <path d={`M 56 165 Q 50 110 100 100 Q 150 110 144 165 L 144 145 Q 130 130 100 128 Q 70 130 56 145 Z`} fill={hair}/>
          <path d={`M 56 165 Q 50 200 64 240 L 56 245 Q 42 200 52 165 Z`} fill={hair}/>
          <path d={`M 144 165 Q 150 200 138 240 L 148 245 Q 158 200 148 165 Z`} fill={hair}/>
        </>
      )}
      {hairShape === "short" && (
        <path d={`M 60 145 Q 65 105 100 100 Q 135 105 140 145 Q 130 125 100 122 Q 70 125 60 145 Z`} fill={hair}/>
      )}
      {hairShape === "punk" && (
        <>
          <path d={`M 60 145 Q 65 100 100 95 Q 135 100 140 145 Q 130 120 100 118 Q 70 120 60 145 Z`} fill={hair}/>
          <path d={`M 75 110 L 80 70 L 90 105`} fill={hsl((h2 + 200) % 360, 90, 60)} />
          <path d={`M 110 105 L 120 75 L 125 110`} fill={hsl((h2 + 200) % 360, 90, 60)} />
        </>
      )}
      {/* highlight on hair */}
      <path d={`M 70 130 Q 90 118 110 122`} stroke={hairLight} strokeWidth="2" fill="none" opacity="0.5"/>

      {/* FACE FEATURES */}
      {/* eyes */}
      <ellipse cx="86" cy="170" rx="3.2" ry="2.2" fill="#1a1208" />
      <ellipse cx="114" cy="170" rx="3.2" ry="2.2" fill="#1a1208" />
      <circle cx="87" cy="169" r="0.8" fill="#fff" opacity="0.8"/>
      <circle cx="115" cy="169" r="0.8" fill="#fff" opacity="0.8"/>
      {/* brows */}
      <path d="M 80 161 Q 86 158 92 160" stroke="#1a1208" strokeWidth="1.5" fill="none" />
      <path d="M 108 160 Q 114 158 120 161" stroke="#1a1208" strokeWidth="1.5" fill="none" />
      {/* nose */}
      <path d={`M 100 175 L 97 188 Q 100 190 103 188`} stroke={skinShadow} strokeWidth="1.2" fill="none" opacity="0.7"/>
      {/* mouth — varied by status */}
      {avatar.status === "LIVE" || avatar.status === "CHAT" ? (
        <path d="M 91 199 Q 100 205 109 199" stroke="#7a3a25" strokeWidth="2" fill={hsl(8, 50, 30)} />
      ) : (
        <path d="M 92 200 Q 100 203 108 200" stroke="#7a3a25" strokeWidth="2" fill="none" />
      )}

      {/* glasses (accessory 0) */}
      {accessory === 0 && (
        <g stroke={hsl(0, 0, 80)} strokeWidth="1.5" fill="none">
          <circle cx="86" cy="170" r="8" />
          <circle cx="114" cy="170" r="8" />
          <path d="M 94 170 L 106 170" />
        </g>
      )}
      {/* headphones (accessory 1, for digital/fitness) */}
      {accessory === 1 && (avatar.cat === "digital" || avatar.cat === "fitness") && (
        <g fill={hsl(0, 0, 30)}>
          <path d={`M 56 160 Q 56 115 100 115 Q 144 115 144 160`} stroke={hsl(0, 0, 30)} strokeWidth="6" fill="none" />
          <rect x="48" y="155" width="14" height="22" rx="6" />
          <rect x="138" y="155" width="14" height="22" rx="6" />
        </g>
      )}
      {/* earrings (accessory 2-3 for female) */}
      {(accessory === 2 || accessory === 3) && avatar.gender === "女" && (
        <>
          <circle cx="58" cy="180" r="2" fill={hsl(h2, 70, 60)} />
          <circle cx="142" cy="180" r="2" fill={hsl(h2, 70, 60)} />
        </>
      )}
      {/* hat */}
      {accessory === 4 && (
        <path d={`M 56 120 Q 100 90 144 120 L 154 130 L 46 130 Z`} fill={hsl((h1 + 90) % 360, 30, 22)} />
      )}

      {/* bottom fade for legibility */}
      <rect width="200" height="356" fill={`url(#fade-${avatar.slug})`} />

      {/* studio noise */}
      <rect width="200" height="356" fill="url(#tv-noise)" opacity="0.05" />
    </svg>
  );
}

/* ===================== Card ===================== */
function AvatarCard({ avatar, onClick }) {
  const cat = window.CATEGORIES.find(c => c.id === avatar.cat);
  const country = window.COUNTRIES[avatar.country];
  // 5/17 founder: 只有小橙有真子页面 (xiaocheng.html), 其他 shop-* 子页面是 mock 假数据,
  // 暂不让跳转 — 卡片就地播 motion 即可.
  const isXiaocheng = avatar.slug === "xiaocheng";
  const portraitRef = useRef(null);

  const inner = (
    <>
      <AvatarPortrait ref={portraitRef} avatar={avatar} />

      {/* top-left tray */}
      <div className="mk-card-tray-l">
        {avatar.pro && <span className="mk-badge mk-badge-pro">PRO</span>}
        {avatar.isNew && <span className="mk-badge mk-badge-new">NEW</span>}
        {avatar.hot && <span className="mk-badge mk-badge-hot">🔥 HOT</span>}
      </div>

      {/* top-right tray */}
      <div className="mk-card-tray-r">
        {avatar.status === "LIVE" && (
          <span className="mk-badge mk-badge-live">
            <span className="dot"/>
            <span>LIVE</span>
          </span>
        )}
        {avatar.status === "CHAT" && (
          <span className="mk-badge mk-badge-hot" style={{ color: "#F69155" }}>💬 互动</span>
        )}
        {avatar.status === "IDLE" && (
          <span className="mk-badge mk-badge-idle">💤 IDLE</span>
        )}
      </div>

      {/* 小橙专属 — 点我 24h CTA hint */}
      {isXiaocheng && (
        <div className="mk-card-cta-hint">
          <span>点我</span>
          <span className="hint-strong">24 小时服务您</span>
        </div>
      )}

      {/* 5/18 r4: shop-NNN "进店" 真打通 — 落 ?mode=interact 直接进真 WS 互动 tab
          (服务端 ws/interact?slug=shop-NNN 已 100% 真 persona: 真名 + 真 opening + qwen-flash 真 Q&A
           + InteractStage 自动接 OSS motion mp4 真嘴动 + 真百炼 sambert 烘焙音频) */}
      {!isXiaocheng && (
        <a
          className="mk-card-chat-pill"
          href={`avatar.html?slug=${avatar.slug}&mode=interact`}
          onClick={(e) => { e.stopPropagation(); }}
          title={`进 ${avatar.name} 的店`}
          aria-label={`进 ${avatar.name} 的店`}
        >
          <i data-lucide="arrow-right-circle"></i>
          <span>进店</span>
        </a>
      )}

      {/* overlay */}
      <div className="mk-card-overlay">
        <div className="mk-card-name">
          <span>{avatar.name}</span>
          <span className="mk-card-flag">{country?.flag}</span>
        </div>
        <div className="mk-card-meta">
          <span className="cat-tag">{cat?.zh}</span>
          <span className="sep">·</span>
          {avatar.status !== "IDLE" ? (
            <span className="viewers">
              <i data-lucide="eye"></i>
              {avatar.viewers >= 1000 ? `${(avatar.viewers/1000).toFixed(1)}k` : avatar.viewers}
            </span>
          ) : (
            <span style={{ color: "var(--mk-fg-3)" }}>等待中</span>
          )}
          <span className="lang-tag">{avatar.langs[0]}</span>
        </div>
      </div>
    </>
  );

  if (isXiaocheng) {
    return (
      <a className="mk-card" href={`avatar.html?slug=${avatar.slug}`} onClick={(e) => {
        if (onClick) { e.preventDefault(); onClick(avatar); }
      }}>{inner}</a>
    );
  }
  // 5/17 非小橙: 点卡片 = portraitRef.toggleAudio() (iOS Safari 必须 user
  // gesture sync 调 v.muted=false + v.play(), 不能仅 React prop)
  return (
    <div className="mk-card mk-card-nonav"
      role="button"
      tabIndex={0}
      onClick={() => portraitRef.current?.toggleAudio()}
    >{inner}</div>
  );
}

/* ===================== Search ===================== */
function SearchBar({ value, onChange }) {
  return (
    <div className="mk-search">
      <span className="mk-search-icon"><i data-lucide="search"></i></span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="搜数字人 · 职业 / 地域 / 风格"
      />
      {!value && (
        <span className="mk-search-kbd">
          <kbd>⌘</kbd><kbd>K</kbd>
        </span>
      )}
    </div>
  );
}

/* ===================== Identity tiers (5/28 v10.87) =====================
   三层身份 (PROPOSAL_tv_home_navigation_5_28.md §4.5):
   - 数字 CEO + 团队: he-cong + 12 CXO (= 13 named, 跟 interact.jsx LIPSYNC_LIVEKIT_SLUGS 去 xiaocheng 后对齐)
   - 数字店长: shop-001..100
   - 数字伙伴: 留位给未来 user-generated avatar
*/
const TEAM_NAMED_SLUGS = new Set([
  "he-cong",
  "lin-zhifan", "sarah-chen", "wang-xueyun", "david-park",
  "su-wanqing", "ahmed-al-rashid", "emily-whitman", "batbayar-tumur",
  "dr-hans-muller", "chen-haoming", "yuki-tanaka", "rajesh-patel",
]);
function isTeamSlug(slug) { return TEAM_NAMED_SLUGS.has(slug); }
function isShopSlug(slug) { return typeof slug === "string" && /^shop-\d{3}$/.test(slug); }

/* ===================== Filter row ===================== */
const QUICK_CHIPS = [
  { id: "all",    label: "全部" },
  { id: "team",   label: "数字 CEO + 团队", special: "identity", identity: "team" },
  { id: "shops",  label: "数字店长",        special: "identity", identity: "shops" },
  { id: "live",   label: "LIVE",   special: "status", status: "LIVE" },
  { id: "chat",   label: "互动中", special: "status", status: "CHAT" },
  { id: "hot",    label: "🔥 高人气" },
  { id: "food",   label: "餐饮", catId: "food" },
  { id: "kanbini",label: "便利店", catId: "kanbini" },
  { id: "beauty", label: "美业", catId: "beauty" },
  { id: "digital",label: "数码", catId: "digital" },
  { id: "trendy", label: "时髦", catId: "trendy" },
  { id: "finance",label: "金融", catId: "finance" },
];

function FilterBar({ active, setActive, sort, setSort, onMore, counts }) {
  return (
    <div className="mk-filterbar">
      <div className="mk-chips" role="tablist">
        {QUICK_CHIPS.map(c => (
          <button
            key={c.id}
            className={`mk-chip ${active === c.id ? "active" : ""} ${c.special === "status" ? "mk-chip-status" : ""}`}
            data-status={c.status}
            onClick={() => setActive(c.id)}
          >
            <span>{c.label}</span>
            {counts[c.id] != null && <span className="mk-chip-count">{counts[c.id]}</span>}
          </button>
        ))}
      </div>
      <div className="mk-filter-right">
        <button className="mk-more-btn" onClick={onMore}>
          <i data-lucide="sliders-horizontal"></i>
          更多筛选
        </button>
        <select className="mk-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="recommended">推荐</option>
          <option value="hot">最热 (24h)</option>
          <option value="new">最新上线</option>
          <option value="fastest">响应最快</option>
        </select>
      </div>
    </div>
  );
}

/* ===================== More Filters Modal ===================== */
function MoreFiltersModal({ open, onClose, filters, setFilters }) {
  if (!open) return null;

  function toggle(group, val) {
    const set = new Set(filters[group]);
    if (set.has(val)) set.delete(val); else set.add(val);
    setFilters({ ...filters, [group]: [...set] });
  }
  function isOn(group, val) { return filters[group].includes(val); }
  function reset() { setFilters({ category: [], gender: [], age: [], country: [], style: [], status: [] }); }

  return (
    <div className="mk-modal-backdrop" onClick={onClose}>
      <div className="mk-modal" onClick={e => e.stopPropagation()}>
        <div className="mk-modal-head">
          <h3>更多筛选</h3>
          <button className="mk-modal-close" onClick={onClose}>
            <i data-lucide="x"></i>
          </button>
        </div>

        <div className="mk-modal-group">
          <h4>商业场景</h4>
          <div className="mk-chips">
            {window.CATEGORIES.map(c => (
              <button key={c.id} className={`mk-chip ${isOn("category", c.id) ? "active" : ""}`}
                onClick={() => toggle("category", c.id)}>{c.zh}</button>
            ))}
          </div>
        </div>

        <div className="mk-modal-group">
          <h4>性别</h4>
          <div className="mk-chips">
            {["女", "男", "非二元"].map(g => (
              <button key={g} className={`mk-chip ${isOn("gender", g) ? "active" : ""}`}
                onClick={() => toggle("gender", g)}>{g}</button>
            ))}
          </div>
        </div>

        <div className="mk-modal-group">
          <h4>年龄</h4>
          <div className="mk-chips">
            {["18-25","25-35","35-45","45-60","60+"].map(a => (
              <button key={a} className={`mk-chip ${isOn("age", a) ? "active" : ""}`}
                onClick={() => toggle("age", a)}>{a}</button>
            ))}
          </div>
        </div>

        <div className="mk-modal-group">
          <h4>国籍</h4>
          <div className="mk-chips">
            {Object.entries(window.COUNTRIES).map(([k, v]) => (
              <button key={k} className={`mk-chip ${isOn("country", k) ? "active" : ""}`}
                onClick={() => toggle("country", k)}>{v.flag} {v.zh}</button>
            ))}
          </div>
        </div>

        <div className="mk-modal-group">
          <h4>风格</h4>
          <div className="mk-chips">
            {Object.entries(window.STYLES).map(([k, v]) => (
              <button key={k} className={`mk-chip ${isOn("style", k) ? "active" : ""}`}
                onClick={() => toggle("style", k)}>{v}</button>
            ))}
          </div>
        </div>

        <div className="mk-modal-footer">
          <button className="mk-modal-reset" onClick={reset}>清除筛选</button>
          <button className="mk-cta-primary" onClick={onClose} style={{ height: 38 }}>查看结果</button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Marketplace App ===================== */
function MarketplaceApp() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("all");
  const [sort, setSort] = useState("recommended");
  const [moreOpen, setMoreOpen] = useState(false);
  const [filters, setFilters] = useState({ category: [], gender: [], age: [], country: [], style: [], status: [] });
  const [visibleCount, setVisibleCount] = useState(18);
  // 单音轨 active slug — 默认全静, 点卡 = unmute 一张, 再点 = 关掉
  const [activeSlug, setActiveSlug] = useState(null);
  const audioCtxValue = useMemo(() => ({ activeSlug, setActiveSlug }), [activeSlug]);

  // shortcut ⌘K
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.querySelector(".mk-search input")?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // re-render icons on every render
  useEffect(() => { if (window.lucide) window.lucide.createIcons(); });

  // filter
  const roster = window.AVATARS_ROSTER;
  const filtered = useMemo(() => {
    let list = [...roster];

    // quick chip
    if (active !== "all") {
      const q = QUICK_CHIPS.find(c => c.id === active);
      if (q?.special === "status") list = list.filter(a => a.status === q.status);
      else if (q?.special === "identity") {
        // 5/28 v10.87 三层身份: team = he-cong + 12 CXO / shops = shop-001..100
        if (q.identity === "team") list = list.filter(a => isTeamSlug(a.slug));
        else if (q.identity === "shops") list = list.filter(a => isShopSlug(a.slug));
      }
      else if (q?.id === "hot") list = list.filter(a => a.hot);
      else if (q?.catId) list = list.filter(a => a.cat === q.catId);
    }

    // more filters
    if (filters.category.length) list = list.filter(a => filters.category.includes(a.cat));
    if (filters.gender.length) list = list.filter(a => filters.gender.includes(a.gender));
    if (filters.country.length) list = list.filter(a => filters.country.includes(a.country));
    if (filters.style.length) list = list.filter(a => filters.style.includes(a.style));
    if (filters.status.length) list = list.filter(a => filters.status.includes(a.status));
    if (filters.age.length) {
      list = list.filter(a => filters.age.some(bucket => {
        const [lo, hi] = bucket.includes("+") ? [60, 999] : bucket.split("-").map(Number);
        return a.age >= lo && a.age <= hi;
      }));
    }

    // search — 5/17 founder: 必须能关键词找到对应数字人.
    // 拆 token 各 token AND, 字段广搜 (name/blurb/tags/cat/country/langs/gender/age/style),
    // 大小写/全半角无关.
    const q = query.trim().toLowerCase();
    if (q) {
      const tokens = q.split(/[\s,，、;；]+/).filter(Boolean);
      list = list.filter(a => {
        const hay = [
          a.name,
          a.blurb,
          ...(a.tags || []),
          window.CATEGORIES.find(c => c.id === a.cat)?.zh,
          window.COUNTRIES[a.country]?.zh,
          ...(a.langs || []),
          a.gender,
          String(a.age || ""),
          window.STYLES?.[a.style],
        ].filter(Boolean).join(" ").toLowerCase();
        return tokens.every(t => hay.includes(t));
      });
    }

    // sort
    if (sort === "hot") list.sort((a, b) => b.viewers - a.viewers);
    else if (sort === "new") list.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    else if (sort === "fastest") list.sort((a, b) => (a.status === "CHAT" ? -1 : 1));
    else {
      // recommended: pro > hot > live > chat > others
      const score = a => (a.pro?4:0) + (a.hot?3:0) + (a.status==="LIVE"?2:0) + (a.status==="CHAT"?1:0) + (a.isMine?100:0);
      list.sort((a, b) => score(b) - score(a));
    }

    return list;
  }, [roster, active, filters, query, sort]);

  // counts per quick chip (against unfiltered + search)
  const counts = useMemo(() => {
    const map = { all: roster.length };
    map.team  = roster.filter(a => isTeamSlug(a.slug)).length;
    map.shops = roster.filter(a => isShopSlug(a.slug)).length;
    map.live  = roster.filter(a => a.status === "LIVE").length;
    map.chat  = roster.filter(a => a.status === "CHAT").length;
    map.hot   = roster.filter(a => a.hot).length;
    window.CATEGORIES.forEach(c => map[c.id] = roster.filter(a => a.cat === c.id).length);
    return map;
  }, [roster]);

  // infinite scroll sentinel
  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current; if (!el) return;
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) setVisibleCount(v => v + 12); });
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // 5/28 v10.87: Hero / CXO grid / 100 店长 section header — 仅 "all" tab + 无任何 search/filter 时显
  // (用户筛选时切换到瀑布纯净模式, 不重复展示三层身份 section).
  const showIdentitySections = active === "all" && !query.trim() &&
    !filters.category.length && !filters.gender.length && !filters.age.length &&
    !filters.country.length && !filters.style.length && !filters.status.length;

  // 5/28 v10.87: hero he-cong + 12 CXO grid 数据源
  const heCongAvatar = useMemo(() => roster.find(a => a.slug === "he-cong"), [roster]);
  const cxoAvatars = useMemo(
    () => roster.filter(a => a.slug !== "he-cong" && isTeamSlug(a.slug)),
    [roster]
  );

  // 5/28 v10.87: 🎲 随机切一位 — 从 LIPSYNC_LIVEKIT_SLUGS 等价 set (named + shop) 抽 random
  const randomPick = useCallback(() => {
    const candidates = roster.filter(a => isTeamSlug(a.slug) || isShopSlug(a.slug));
    if (!candidates.length) return;
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    window.location.href = `avatar.html?slug=${pick.slug}&mode=interact`;
  }, [roster]);

  // 5/28 v10.87: hero+CXO grid 显示时, 瀑布隐藏 he-cong + 12 CXO (避免双重展示), 只显数字店长 + 其他.
  // 切到 "数字 CEO+团队" tab 时 hero/CXO 不显, 瀑布显全 team 数据 (filter 已限定 team).
  const waterfallList = useMemo(() => {
    if (showIdentitySections) return filtered.filter(a => !isTeamSlug(a.slug));
    return filtered;
  }, [filtered, showIdentitySections]);

  const visible = waterfallList.slice(0, visibleCount);
  const hasMore = waterfallList.length > visible.length;

  return (
    <AudioCtx.Provider value={audioCtxValue}>
      <div className="mk-shell">
        <header className="mk-topbar">
          <div className="mk-topbar-row">
            <a href="index.html" className="mk-brand" style={{ textDecoration: "none", color: "inherit" }}>
              <div className="mk-brand-mark"><img src="assets/wanwu-deer.png" alt=""/></div>
              <div className="mk-brand-text">
                <span className="mk-brand-zh">万物 AI 频道</span>
                <span className="mk-brand-sub">AI 数字人实时交互生态</span>
              </div>
            </a>

            <div className="mk-topbar-right">
              <SearchBar value={query} onChange={setQuery} />
              {/* 5/28 v10.87 §4.4 🎲 随机切一位 — 1 键体验任一数字人 */}
              <button
                className="mk-random-btn"
                onClick={randomPick}
                title="随机切一位数字人"
                aria-label="随机切一位数字人"
              >
                <span className="mk-random-emoji" aria-hidden="true">🎲</span>
                <span className="mk-random-text">随机切一位</span>
              </button>
            </div>
          </div>

          <FilterBar
            active={active} setActive={setActive}
            sort={sort} setSort={setSort}
            onMore={() => setMoreOpen(true)}
            counts={counts}
          />
        </header>

        {/* 5/28 v10.87 §4.1: Hero — 数字 CEO 何聪 prominent CTA */}
        {showIdentitySections && heCongAvatar && (
          <section className="mk-hero" aria-label="数字 CEO 何聪">
            <a className="mk-hero-card" href={`avatar.html?slug=he-cong&mode=interact`}>
              <div className="mk-hero-portrait">
                <img
                  src={heCongAvatar.portrait}
                  alt={heCongAvatar.name}
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                />
                <div className="mk-hero-portrait-fade" />
              </div>
              <div className="mk-hero-body">
                <div className="mk-hero-badges">
                  <span className="mk-hero-badge mk-hero-badge-ceo">数字 CEO</span>
                  <span className="mk-hero-badge mk-hero-badge-live">
                    <span className="mk-hero-live-dot" /> LIVE · 24h
                  </span>
                  <span className="mk-hero-badge mk-hero-badge-pro">PRO</span>
                </div>
                <h2 className="mk-hero-name">
                  {heCongAvatar.name}
                  <span className="mk-hero-flag">🇨🇳</span>
                </h2>
                <p className="mk-hero-tagline">
                  「 探胚上胚 · AI 数字生命的第一推力 」
                </p>
                <p className="mk-hero-blurb">{heCongAvatar.blurb || "探针集团创始人 · 数字 CEO · 24h 在线"}</p>
                <div className="mk-hero-cta-row">
                  <span className="mk-hero-cta-primary">
                    ▶ 跟何聪通话
                  </span>
                  <span className="mk-hero-cta-meta">
                    SoulX-FlashHead 实时面对面 · 中英双语
                  </span>
                </div>
              </div>
            </a>
          </section>
        )}

        {/* 5/28 v10.87 §4.2: 数字团队 12 CXO grid */}
        {showIdentitySections && cxoAvatars.length > 0 && (
          <section className="mk-section" aria-label="数字团队 12 CXO">
            <div className="mk-section-head">
              <h2 className="mk-section-title">
                <span className="mk-section-num">{cxoAvatars.length}</span>
                <span>位 CXO · 数字团队</span>
              </h2>
              <p className="mk-section-sub">CTO / CPO / COO / CFO / CMO / CHRO / CSO / CRO / CAIO / CSeO / CCommO / CTokO — 各司其职</p>
            </div>
            <div className="mk-cxo-grid">
              {cxoAvatars.map(a => <AvatarCard key={a.slug} avatar={a} />)}
            </div>
          </section>
        )}

        {/* 5/28 v10.87 §4.3: 100 数字店长 section header */}
        {showIdentitySections && (
          <section className="mk-section mk-section-shophead" aria-label="数字店长 千行百业">
            <div className="mk-section-head">
              <h2 className="mk-section-title">
                <span className="mk-section-num">{counts.shops || 100}</span>
                <span>位数字店长 · 千行百业</span>
              </h2>
              <p className="mk-section-sub">餐饮 · 便利店 · 美业 · 数码 · 时髦 · 金融 — 每天持续增长</p>
            </div>
          </section>
        )}

        {!showIdentitySections && (
          <section className="mk-resultmeta">
            <h1>
              <span style={{ color: "var(--mk-fg-2)", fontWeight: 400, marginRight: 8 }}>已上线</span>
              <span className="count">{waterfallList.length}</span>
              <span style={{ color: "var(--mk-fg-2)", fontWeight: 400, marginLeft: 8 }}>位数字人 · 每天持续增长中……</span>
            </h1>
            <span className="sub">
              {window.ROSTER_STATS.online} 位在线 · 累计观看 {window.ROSTER_STATS.hours.toLocaleString()} 小时
            </span>
          </section>
        )}

        <section className="mk-grid">
          {visible.length === 0 && (
            <div className="mk-empty">
              <i data-lucide="search-x"></i>
              <h3>没有找到匹配的数字人</h3>
              <p>试试清除筛选条件, 或换个关键词搜索</p>
            </div>
          )}
          {visible.map(a => <AvatarCard key={a.slug} avatar={a} />)}
          {hasMore && Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} className="mk-skeleton"/>
          ))}
        </section>

        {hasMore && <div ref={sentinel} style={{ height: 1 }}/>}

        <footer className="mk-footer">
          <div className="mk-footer-block">
            <div className="mk-footer-copyright">© 2026 万物数字店长</div>
            <div className="mk-footer-org">所有权和解释权: <strong>Probe Group Pte. Ltd.</strong> (新加坡探针集团)</div>
            <div className="mk-footer-org">中国市场运营方: <strong>探针能源 (新疆) 有限公司</strong></div>
            <div className="mk-footer-org">AI 技术支持方: <strong>ProAI LLC</strong> (蒙古)</div>
            <div className="mk-footer-warn">内容受版权保护 · 仅限内部观摩 · 严禁下载传播</div>
          </div>
        </footer>
      </div>

      <MoreFiltersModal open={moreOpen} onClose={() => setMoreOpen(false)} filters={filters} setFilters={setFilters} />

      {/* shared SVG defs */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <filter id="tv-noise">
            <feTurbulence baseFrequency="0.9" numOctaves="2" />
            <feColorMatrix values="0 0 0 0 1   0 0 0 0 1   0 0 0 0 1   0 0 0 1 0" />
          </filter>
        </defs>
      </svg>
    </AudioCtx.Provider>
  );
}

// 5/18 r3: 全局 toast helper (mk-card-chat-pill 禁用提示用)
if (typeof window !== "undefined" && !window.__mkToast) {
  let _toastEl = null, _toastTimer = null;
  window.__mkToast = function (msg) {
    if (!_toastEl) {
      _toastEl = document.createElement("div");
      _toastEl.className = "mk-toast";
      document.body.appendChild(_toastEl);
    }
    _toastEl.textContent = msg;
    _toastEl.classList.add("mk-toast-show");
    if (_toastTimer) clearTimeout(_toastTimer);
    _toastTimer = setTimeout(() => _toastEl.classList.remove("mk-toast-show"), 1900);
  };
}

// Only mount the marketplace if this file is being used as the page root
// (i.e. on index.html). Avatar.html imports this file just for the components.
Object.assign(window, { AvatarCard, AvatarPortrait, GenerativePortrait, MarketplaceApp, TvAudioCtx: AudioCtx });

if (document.body.classList.contains("mk")) {
  ReactDOM.createRoot(document.getElementById("root")).render(<MarketplaceApp />);
}
